import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Divider,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Chip,
  Switch,
  FormControlLabel,
  Alert,
  Paper,
} from "@mui/material";
import { AccessTime as ClockIcon } from "@mui/icons-material";

const SCHEDULE_TYPES = [
  { value: "once", label: "Run Once" },
  { value: "recurring", label: "Recurring Schedule" },
];

const FREQUENCY_OPTIONS = [
  { value: "minutes", label: "Minutes" },
  { value: "hourly", label: "Hourly" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

const WEEKDAYS = [
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
  { value: "saturday", label: "Saturday" },
  { value: "sunday", label: "Sunday" },
];

const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

export const TimeTriggerModal = ({ open, node }) => {
  const nodeData = node?.data || {};
  const existingSettings = nodeData?.output?.settings || {};

  const [scheduleType, setScheduleType] = useState(existingSettings.scheduleType || "once");
  const [startDateTime, setStartDateTime] = useState(existingSettings.startDateTime || "");
  const [endDateTime, setEndDateTime] = useState(existingSettings.endDateTime || "");
  const [frequency, setFrequency] = useState(existingSettings.frequency || "daily");
  const [interval, setInterval] = useState(existingSettings.interval || 1);
  const [selectedWeekdays, setSelectedWeekdays] = useState(existingSettings.selectedWeekdays || []);
  const [selectedMonths, setSelectedMonths] = useState(existingSettings.selectedMonths || []);
  const [dayOfMonth, setDayOfMonth] = useState(existingSettings.dayOfMonth || 1);
  const [timeOfDay, setTimeOfDay] = useState(existingSettings.timeOfDay || "09:00");
  const [isEnabled, setIsEnabled] = useState(existingSettings.isEnabled !== false);
  const [timezone, setTimezone] = useState(existingSettings.timezone || "UTC");
  const [description, setDescription] = useState(existingSettings.description || "");

  // Generate human-readable schedule description
  const generateScheduleDescription = () => {
    if (scheduleType === "once") {
      return `Run once on ${startDateTime ? new Date(startDateTime).toLocaleString() : "selected date"}`;
    }

    let desc = `Run every `;

    if (frequency === "minutes") {
      desc += `${interval} minute${interval !== 1 ? "s" : ""}`;
    } else if (frequency === "hourly") {
      desc += `${interval} hour${interval !== 1 ? "s" : ""}`;
    } else if (frequency === "daily") {
      desc += `${interval} day${interval !== 1 ? "s" : ""} at ${timeOfDay}`;
    } else if (frequency === "weekly") {
      desc += `${interval} week${interval !== 1 ? "s" : ""} on `;
      desc +=
        selectedWeekdays.length > 0
          ? selectedWeekdays.map((day) => WEEKDAYS.find((w) => w.value === day)?.label).join(", ")
          : "selected days";
      desc += ` at ${timeOfDay}`;
    } else if (frequency === "monthly") {
      desc += `${interval} month${interval !== 1 ? "s" : ""} on day ${dayOfMonth} at ${timeOfDay}`;
      if (selectedMonths.length > 0 && selectedMonths.length < 12) {
        desc += ` in ${selectedMonths.map((m) => MONTHS.find((month) => month.value === m)?.label).join(", ")}`;
      }
    }

    if (endDateTime) {
      desc += ` until ${new Date(endDateTime).toLocaleString()}`;
    }

    return desc;
  };

  // Generate cron expression
  const generateCronExpression = () => {
    if (scheduleType === "once") {
      // For one-time execution, we'll use a specific datetime
      if (!startDateTime) return "";
      const date = new Date(startDateTime);
      return `${date.getMinutes()} ${date.getHours()} ${date.getDate()} ${date.getMonth() + 1} *`;
    }

    const [hour, minute] = timeOfDay.split(":").map(Number);

    switch (frequency) {
      case "minutes":
        return `*/${interval} * * * *`;
      case "hourly":
        return `${minute} */${interval} * * *`;
      case "daily":
        return `${minute} ${hour} */${interval} * *`;
      case "weekly":
        const weekdayNumbers = selectedWeekdays
          .map((day) => {
            const index = WEEKDAYS.findIndex((w) => w.value === day);
            return index === 6 ? 0 : index + 1; // Convert Sunday to 0
          })
          .join(",");
        return `${minute} ${hour} * * ${weekdayNumbers || "*"}`;
      case "monthly":
        const months =
          selectedMonths.length > 0 && selectedMonths.length < 12 ? selectedMonths.join(",") : "*";
        return `${minute} ${hour} ${dayOfMonth} ${months} *`;
      default:
        return `${minute} ${hour} * * *`;
    }
  };

  // Persist settings to node.data.output automatically
  useEffect(() => {
    if (!node?.data?.setData) return;

    const scheduleSettings = {
      scheduleType,
      startDateTime,
      endDateTime,
      frequency,
      interval,
      selectedWeekdays,
      selectedMonths,
      dayOfMonth,
      timeOfDay,
      isEnabled,
      timezone,
      description,
      cronExpression: generateCronExpression(),
      humanReadable: generateScheduleDescription(),
    };

    node.data.setData((oldData) => ({
      ...oldData,
      output: {
        ...oldData.output,
        settings: scheduleSettings,
      },
    }));
  }, [
    scheduleType,
    startDateTime,
    endDateTime,
    frequency,
    interval,
    selectedWeekdays,
    selectedMonths,
    dayOfMonth,
    timeOfDay,
    isEnabled,
    timezone,
    description,
    node?.data?.setData,
  ]);

  const handleWeekdayToggle = (weekday) => {
    setSelectedWeekdays((prev) =>
      prev.includes(weekday) ? prev.filter((w) => w !== weekday) : [...prev, weekday]
    );
  };

  const handleMonthToggle = (month) => {
    setSelectedMonths((prev) =>
      prev.includes(month) ? prev.filter((m) => m !== month) : [...prev, month]
    );
  };

  return (
    <Box sx={{ marginTop: "20px" }}>
      {/* Schedule Type */}
      <FormControl fullWidth variant="outlined" sx={{ mb: 3 }}>
        <InputLabel>Schedule Type</InputLabel>
        <Select
          value={scheduleType}
          onChange={(e) => setScheduleType(e.target.value)}
          label="Schedule Type"
          sx={{
            backgroundColor: "#2A2A2A",
            color: "#CCC",
          }}
        >
          {SCHEDULE_TYPES.map((type) => (
            <MenuItem key={type.value} value={type.value}>
              {type.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* One-time execution settings */}
      {scheduleType === "once" && (
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Execution Date & Time"
            type="datetime-local"
            value={startDateTime}
            onChange={(e) => setStartDateTime(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{
              backgroundColor: "#2A2A2A",
              "& .MuiOutlinedInput-root": {
                color: "#CCC",
              },
            }}
          />
        </Box>
      )}

      {/* Recurring execution settings */}
      {scheduleType === "recurring" && (
        <>
          {/* Frequency Selection */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={8}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Frequency</InputLabel>
                <Select
                  value={frequency}
                  onChange={(e) => setFrequency(e.target.value)}
                  label="Frequency"
                  sx={{
                    backgroundColor: "#2A2A2A",
                    color: "#CCC",
                  }}
                >
                  {FREQUENCY_OPTIONS.map((freq) => (
                    <MenuItem key={freq.value} value={freq.value}>
                      {freq.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Every"
                type="number"
                value={interval}
                onChange={(e) => setInterval(Math.max(1, parseInt(e.target.value) || 1))}
                inputProps={{ min: 1 }}
                sx={{
                  backgroundColor: "#2A2A2A",
                  "& .MuiOutlinedInput-root": {
                    color: "#CCC",
                  },
                }}
              />
            </Grid>
          </Grid>

          {/* Time of day (for daily, weekly, monthly) */}
          {["daily", "weekly", "monthly"].includes(frequency) && (
            <TextField
              fullWidth
              label="Time of Day"
              type="time"
              value={timeOfDay}
              onChange={(e) => setTimeOfDay(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                mb: 3,
                backgroundColor: "#2A2A2A",
                "& .MuiOutlinedInput-root": {
                  color: "#CCC",
                },
              }}
            />
          )}

          {/* Weekly options */}
          {frequency === "weekly" && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: "#CCC" }}>
                Days of Week
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "nowrap",
                  gap: 0.5,
                  overflowX: "auto",
                  pb: 1,
                }}
              >
                {WEEKDAYS.map((day) => (
                  <Chip
                    key={day.value}
                    label={day.label.substring(0, 3)}
                    variant={selectedWeekdays.includes(day.value) ? "filled" : "outlined"}
                    color={selectedWeekdays.includes(day.value) ? "primary" : "default"}
                    onClick={() => handleWeekdayToggle(day.value)}
                    clickable
                    size="small"
                    sx={{
                      minWidth: "45px",
                      backgroundColor: selectedWeekdays.includes(day.value) ? "#FF6C58" : "#2A2A2A",
                      color: selectedWeekdays.includes(day.value) ? "#FFF" : "#CCC",
                      borderColor: "#555",
                      "&:hover": {
                        backgroundColor: selectedWeekdays.includes(day.value) ? "#FF5A47" : "#333",
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Monthly options */}
          {frequency === "monthly" && (
            <>
              <TextField
                fullWidth
                label="Day of Month"
                type="number"
                value={dayOfMonth}
                onChange={(e) =>
                  setDayOfMonth(Math.max(1, Math.min(31, parseInt(e.target.value) || 1)))
                }
                inputProps={{ min: 1, max: 31 }}
                sx={{
                  mb: 2,
                  backgroundColor: "#2A2A2A",
                  "& .MuiOutlinedInput-root": {
                    color: "#CCC",
                  },
                }}
              />
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: "#CCC" }}>
                  Months (leave empty for all months)
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {MONTHS.map((month) => (
                    <Chip
                      key={month.value}
                      label={month.label.substring(0, 3)}
                      size="small"
                      variant={selectedMonths.includes(month.value) ? "filled" : "outlined"}
                      color={selectedMonths.includes(month.value) ? "primary" : "default"}
                      onClick={() => handleMonthToggle(month.value)}
                      clickable
                      sx={{
                        minWidth: "40px",
                        backgroundColor: selectedMonths.includes(month.value)
                          ? "#FF6C58"
                          : "#2A2A2A",
                        color: selectedMonths.includes(month.value) ? "#FFF" : "#CCC",
                        borderColor: "#555",
                        "&:hover": {
                          backgroundColor: selectedMonths.includes(month.value)
                            ? "#FF5A47"
                            : "#333",
                        },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </>
          )}

          {/* Start and End Date */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Start Date (Optional)"
                type="date"
                value={startDateTime.split("T")[0] || ""}
                onChange={(e) =>
                  setStartDateTime(
                    e.target.value ? `${e.target.value}T${timeOfDay || "09:00"}` : ""
                  )
                }
                InputLabelProps={{ shrink: true }}
                sx={{
                  backgroundColor: "#2A2A2A",
                  "& .MuiOutlinedInput-root": {
                    color: "#CCC",
                  },
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="End Date (Optional)"
                type="date"
                value={endDateTime.split("T")[0] || ""}
                onChange={(e) =>
                  setEndDateTime(e.target.value ? `${e.target.value}T${timeOfDay || "09:00"}` : "")
                }
                InputLabelProps={{ shrink: true }}
                sx={{
                  backgroundColor: "#2A2A2A",
                  "& .MuiOutlinedInput-root": {
                    color: "#CCC",
                  },
                }}
              />
            </Grid>
          </Grid>
        </>
      )}

      {/* Timezone */}
      <TextField
        fullWidth
        label="Timezone"
        value={timezone}
        onChange={(e) => setTimezone(e.target.value)}
        placeholder="e.g., UTC, America/New_York, Europe/London"
        sx={{
          mb: 3,
          backgroundColor: "#2A2A2A",
          "& .MuiOutlinedInput-root": {
            color: "#CCC",
          },
        }}
      />

      {/* Description */}
      <TextField
        fullWidth
        label="Description (Optional)"
        multiline
        rows={2}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Describe what this schedule does..."
        sx={{
          mb: 3,
          backgroundColor: "#2A2A2A",
          "& .MuiOutlinedInput-root": {
            color: "#CCC",
          },
        }}
      />

      {/* Enable/Disable */}
      <FormControlLabel
        control={
          <Switch
            checked={isEnabled}
            onChange={(e) => setIsEnabled(e.target.checked)}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": {
                color: "#FF6C58",
              },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: "#FF6C58",
              },
            }}
          />
        }
        label="Enable Schedule"
        sx={{
          mb: 3,
          "& .MuiFormControlLabel-label": {
            color: "#CCC",
          },
        }}
      />
    </Box>
  );
};
